import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PaymentTable } from '@/components/payment-table';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { usePayments } from '@/hooks/use-payments';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PAID', label: 'Paid' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PAID_CALLBACK_FAILED', label: 'Paid (Callback Failed)' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'FAILED', label: 'Failed' },
];

const BANK_OPTIONS = [
  { value: '', label: 'All Banks' },
  { value: 'DBS', label: 'DBS' },
  { value: 'UOB', label: 'UOB' },
];

const PAGE_SIZE = 20;

export function PaymentsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading, error } = usePayments({
    status: statusFilter || undefined,
    bank: bankFilter || undefined,
    page,
    size: PAGE_SIZE,
  });

  useEffect(() => {
    setPage(0);
  }, [statusFilter, bankFilter]);

  const payments = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Payments</h2>
        <p className="mt-1 text-sm text-slate-500">
          All payment requests — auto-refreshes every 15s
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Payment Requests</CardTitle>
            <div className="flex gap-2">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-48"
                aria-label="Filter by status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <Select
                value={bankFilter}
                onChange={(e) => setBankFilter(e.target.value)}
                className="w-32"
                aria-label="Filter by bank"
              >
                {BANK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 pb-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-400">
              Loading...
            </div>
          ) : error ? (
            <div className="px-6 py-4 text-sm text-red-600">
              {error instanceof Error ? error.message : 'Failed to load payments'}
            </div>
          ) : (
            <PaymentTable payments={payments} />
          )}
        </CardContent>
      </Card>

      {!isLoading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)} of{' '}
            {totalElements} payments
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
